<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'

definePageMeta({
    layout: 'plain',
    middleware: ['landing-page'],
})

const { site } = useCoreConfig()
const { data: pageData } = await usePageData('frontpage')

const { getHead, getSeoMeta } = useStrapiSeo(
    pageData.value?.seo || {
        metaTitle: `Spil sjove spilleautomater hos ${site.title} Online Casino`,
        metaDescription: `Bliv medlem af ${site.title}™ i dag. Spil eksklusive spilleautomater online. Vilkår gælder. 18+. Spil ansvarligt.`,
    }
)

const { signupBundles } = useSignupBundles()
const { isFromPartner } = usePartner()

// NOTE:
// Fallback Content is set in case no Strapi data is available
// (this will be the case until Strapi is seeded and updated on all environments)
const headerContent = {
    gameCards: [
        {
            title: 'Starburst',
            bg: '/game_tiles/evolution/netent/starburst/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/starburst-assets.png',
            logo: '/game_tiles/evolution/netent/starburst/spilnu/game_logo.png',
            slug: 'starburst',
            videoId: 'c3a7bbf30b2d402fabd82948e6e4454b',
        },
        {
            title: '12 Masks of Fire Drum Frenzy',
            bg: '/game_tiles/games-global/gameburger-studios/12-masks-of-fire-drum-frenzy/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/12-masks-of-fire-drum-frenzy_asset.png',
            logo: '/game_tiles/games-global/gameburger-studios/12-masks-of-fire-drum-frenzy/spilnu/game_logo.png',
            slug: '12-masks-of-fire-drum-frenzy',
            videoId: '1e9d7ddc2fda5f4765984107288390e2',
        },
        {
            title: 'Gold Blitz Ultimate',
            bg: '/game_tiles/games-global/fortune-factory-studios/gold-blitz-ultimate/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/gold-blitz-ultimate_asset.png',
            logo: '/game_tiles/games-global/fortune-factory-studios/gold-blitz-ultimate/spilnu/game_logo.png',
            slug: 'gold-blitz-ultimate',
            videoId: '9939217e7538a708c24238fa8d8c387f',
        },
        {
            title: 'Lightning Roulette',
            bg: '/game_tiles/evolution/evolution/lightning-roulette/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/lightning-roulette-assets.png',
            logo: '/game_tiles/evolution/evolution/lightning-roulette/spilnu/game_logo.png',
            slug: 'lightning-roulette',
            videoId: '33476edb2a4e5312ca6d850f46285e89',
        }
    ]
}

useHead(getHead())
useSeoMeta(getSeoMeta())
</script>

<template>
    <div class="landing-page">
        <LandingPageNavBar fallback-path="/lobby" />

        <main
            class="landing-page__main"
            data-test="welcome-page"
        >
            <LandingPageHeader :content="headerContent as any" :offers="signupBundles" />

            <StickyTerms
                :title="isFromPartner ? 'Bonus vilkår' : 'Vigtige Betingelser'"
                is-landing-page
                :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 50, backgroundColor: '#041135' }"
            />

            <LazyVisibleNimbusStrapiDynamicRenderer
                v-if="!isEmpty(pageData?.dynamicContent)"
                :content="pageData?.dynamicContent"
                :max-width="1024"
                hydrate-on-visible
            />

            <section class="section section--dark bonus-terms">
                <div class="section__content">
                    <StickyTerms
                        title="Bonus vilkår"
                        is-landing-page
                        force-full-terms
                    />
                </div>
            </section>
        </main>
        <LazyVisibleFooterContainer :enable-terms="false" hydrate-on-visible />
    </div>
</template>

<style scoped lang="scss">
.landing-page {
    --sticky-terms-color: #fff;

    &__main {
        a:not(.btn--cta) {
            color: inherit;
        }
    }

    .container--lg {
        @media (min-width: $screen-xl) {
            max-width: $screen-lg;
        }
    }

    :deep(.skeleton-list__headline),
    :deep(.game-section h1) {
        text-align: center;
        margin-bottom: 30px;

        font-size: 8.88vw;
        font-size: clamp(28px, 8.88vw, 34px);
        font-weight: 900;

        @media (min-width: $screen-sm-md) {
            br {
                display: none;
            }
        }

        @media (min-width: $screen-md) {
            font-size: 38px;
        }
    }

    :deep(.game-list__section) {
        transform: translateY(-30px);
    }

    :deep(.game-list__title) {
        font-size: 36px;
        font-weight: 900;
        text-align: center;

        @media (min-width: $screen-xs) {
            font-size: 42px;
        }

        @media (min-width: $screen-md) {
            font-size: 46px;
            margin-bottom: 20px;
        }
    }
}

.dynamic-content {
    padding: 0 15px;

    > :deep(div:has(.usp-bar, .winners-sidebar)) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    > :deep(div:not(.panel__body)) {
        padding: 40px 0;

        @media (min-width: $screen-lg) {
            padding: 50px 0;
        }
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }
}

.bonus-terms {
    margin-top: 20px;

    @include tablet {
        margin-top: 40px;
    }
}

.footer {
    max-width: 1024px;
    margin: 25px auto 0;
    padding: 30px 15px;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
        padding: 30px;
    }
}
</style>
